import { FunctionComponent, memo } from "react";
import TagList from "../tag-list";
import { Select } from "@edgetier/select";
import usePermission from "hooks-for/permissions/use-permission";
import RemoveTagGroupButton from "./remove-tag-group-button";
import { IProps } from "./tag-group.types";
import { ITag, Permission, TagGroupType } from "@edgetier/watchtower-types";
import { EDIT_INTERACTION_TAGS_ENABLED } from "constants/feature-flags";
import useTagGroups from "hooks-for/tag-groups/use-tag-groups";

import "./tag-group.scss";

/**
 * Display tag group with a tag list.
 * @param props.groupTags       All tags from tag group
 * @param props.interactionTags Interaction tags from tag group
 * @param props.tagGroup        Tag group label
 * @param props.removeTagGroup  Callback to remove a tag group
 * @param props.updateInteractionTags Callback for passing new updated tags
 */
const TagGroup: FunctionComponent<IProps> = ({
    interactionTags,
    groupTags,
    tagGroup,
    updateInteractionTags,
    removeTagGroup,
}) => {
    const { data: tagGroupDetails } = useTagGroups(
        {},
        {
            select: (groups) => groups.find((group) => group.tagGroup === tagGroup),
        }
    );
    const { hasPermission } = usePermission(Permission.ManageInteractionTags);

    const allowTagGroupEdit =
        EDIT_INTERACTION_TAGS_ENABLED && hasPermission && tagGroupDetails?.tagGroupType === TagGroupType.PhraseSearch;

    return (
        <div className="tag-group">
            <div className="tag-group__header">
                <label htmlFor={tagGroup} className="tag-group__title">
                    {tagGroup}
                </label>
                {allowTagGroupEdit && <RemoveTagGroupButton tagGroup={tagGroup} removeTagGroup={removeTagGroup} />}
            </div>

            {allowTagGroupEdit ? (
                <Select<ITag, any>
                    labelledBy={tagGroup}
                    items={groupTags}
                    onSelect={(tagIds: string | string[]) => {
                        return Array.isArray(tagIds)
                            ? updateInteractionTags(tagGroup, tagIds)
                            : updateInteractionTags(tagGroup, [tagIds]);
                    }}
                    selectedValues={interactionTags.map(({ tagId }) => tagId) ?? []}
                    description="tag"
                    isSingleSelect={
                        typeof tagGroupDetails !== "undefined" ? tagGroupDetails.isUnaryForInteractions : false
                    }
                    getLabel={({ tag }) => tag}
                    getValue={({ tagId }) => tagId}
                    previewPlaceholder={
                        <>{interactionTags.length > 0 ? <TagList tags={interactionTags} /> : "No tags selected"}</>
                    }
                />
            ) : (
                <TagList tags={interactionTags} />
            )}
        </div>
    );
};

export default memo(TagGroup);
